import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { NgxCurrencyDirective } from 'ngx-currency';
import { FooterActionComponent } from '../../../../shared/components/footer-action/footer-action.component';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { LoadingBoxComponent } from '../../../../shared/components/loading-box/loading-box.component';
import { PageScrollSpyComponent, ScrollSpySection } from '../../../../shared/components/page-scroll-spy/page-scroll-spy.component';
import { SelectPesquisavelComponent } from '../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { MaskService } from '../../../../shared/services/mask.service';
import { UtilService } from '../../../../shared/services/util.service';
import { AbntPipe } from '../../../../shared/pipes/abnt.pipe';
import { CondicaoPagamentoService } from '../../../../shared/services/apis/financas/condicao-pagamento.service';
import { CondicaoPagamentoRequest } from '../../../../shared/services/requests/financas/condicao-pagamento.request';
import { CondicaoPagamentoLookupOpcaoStruct } from '../../../../shared/services/structs/financas/condicao-pagamento.struct';
import { CondicaoPagamentoModoEnum } from '../../../../shared/enums/financas/condicao-pagamento-modo.enum';

const VALOR_REFERENCIA_PREVIA = 1200;

interface ModoCard {
  id: number;
  label: string;
  hint: string;
  icon: string;
}

interface ParcelaPrevia {
  rotulo: string;
  data: string;
  valor: number;
  diasLabel: string;
}

@Component({
  selector: 'app-condicao-pagamento-cadastro',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatSlideToggleModule,
    NgxCurrencyDirective,
    HeaderComponent,
    LoadingBoxComponent,
    FooterActionComponent,
    PageScrollSpyComponent,
    SelectPesquisavelComponent,
    AbntPipe,
  ],
  templateUrl: './condicao-pagamento-cadastro.component.html',
  styleUrl: './condicao-pagamento-cadastro.component.css',
})
export class CondicaoPagamentoCadastroComponent implements OnInit {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Financas;

  public form!: FormGroup;
  public isLoading = false;
  public isSaving = false;
  public isEdicao = false;
  public idCondicaoPagamento = 0;

  public formasRecebimentoLookup: CondicaoPagamentoLookupOpcaoStruct[] = [];

  public modosDisponiveis: ModoCard[] = [
    { id: CondicaoPagamentoModoEnum.IntervaloUnico, label: 'Intervalo único', hint: 'Mesmo prazo entre todas as parcelas — equivale ao "Nx".', icon: 'repeat' },
    { id: CondicaoPagamentoModoEnum.DiasPorParcela, label: 'Dias por parcela', hint: 'Prazo informado parcela a parcela — "30 60 90", "15 +2x".', icon: 'format_list_numbered' },
  ];

  public sections: ScrollSpySection[] = [
    { id: 'identificacao', label: 'Identificação' },
    { id: 'parcelas', label: 'Estrutura de parcelas' },
    { id: 'formas', label: 'Formas aplicáveis' },
  ];

  // Máscaras default do MaskService — allowNegative:false explícito (convenção já usada em várias
  // telas, ex.: contas-pagar-cadastro/contas-receber-cadastro), já que nenhum destes campos aceita
  // valor negativo (parcelas, dias, taxa de juros).
  public integerMask: any;
  public percentMask: any;

  constructor(
    private formBuilder: FormBuilder,
    private condicaoPagamentoService: CondicaoPagamentoService,
    private alertService: AlertService,
    private router: Router,
    private route: ActivatedRoute,
    private maskService: MaskService
  ) {
    this.integerMask = { ...this.maskService.getIntegerMask(), allowNegative: false };
    this.percentMask = { ...this.maskService.getPercentMask(2), allowNegative: false };
  }

  ngOnInit(): void {
    this.createForm();
    this.carregarLookup();

    this.form.get('idCondicaoPagamentoModo')?.valueChanges.subscribe(() => this.onModoChange());
    this.form.get('numeroParcelas')?.valueChanges.subscribe(() => this.onNumeroParcelasChange());

    this.route.params.subscribe(params => {
      const idParam = params['idCondicaoPagamento'];
      const id = Number(idParam);

      this.idCondicaoPagamento = id;
      this.isEdicao = !!idParam && !Number.isNaN(id) && id > 0;

      if (this.isEdicao) {
        this.carregarCondicaoPagamento();
      }
    });
  }

  private createForm(): void {
    this.form = this.formBuilder.group({
      // Validators.pattern além de required: um nome só com espaços em branco passa no required
      // (string não-vazia), mas não deveria ser aceito — work item 147774, achado 6.
      nome: ['', [Validators.required, Validators.pattern(/\S/)]],
      idCondicaoPagamentoModo: [CondicaoPagamentoModoEnum.IntervaloUnico],
      numeroParcelas: [3, [Validators.required, Validators.min(1)]],
      intervaloDias: [30],
      dias: this.formBuilder.array([]),
      taxaJuros: [0],
      isAtivo: [true],
      idsFormaRecebimento: new FormControl<number[]>([]),
    });
  }

  private carregarLookup(): void {
    this.condicaoPagamentoService.getLookup().subscribe({
      next: res => {
        if (res.isError) {
          this.alertService.show('Atenção', res.errorDescription || 'Erro ao carregar formas de recebimento aplicáveis.', AlertType.warning);
          return;
        }
        this.formasRecebimentoLookup = res.listaFormaRecebimento || [];
      },
      error: () => {
        this.alertService.show('Erro', 'Erro ao carregar formas de recebimento aplicáveis.', AlertType.error);
      }
    });
  }

  private carregarCondicaoPagamento(): void {
    this.isLoading = true;
    this.condicaoPagamentoService.getById(this.idCondicaoPagamento).subscribe({
      next: res => {
        this.isLoading = false;
        if (res.isError || !res.condicaoPagamento) {
          this.alertService.show('Atenção', res.errorDescription || 'Condição de pagamento não encontrada.', AlertType.warning);
          this.voltar();
          return;
        }

        const condicao = res.condicaoPagamento;
        this.form.patchValue({
          nome: condicao.nome,
          idCondicaoPagamentoModo: condicao.idCondicaoPagamentoModo,
          numeroParcelas: condicao.numeroParcelas,
          intervaloDias: condicao.intervaloDias,
          taxaJuros: condicao.taxaJuros,
          isAtivo: condicao.isAtivo,
          idsFormaRecebimento: condicao.formasAplicaveis.map(f => f.idFormaRecebimento),
        });

        this.diasArray.clear();
        condicao.diasPorParcela
          .slice()
          .sort((a, b) => a.ordem - b.ordem)
          .forEach(p => this.diasArray.push(this.formBuilder.control(p.dias)));
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Erro ao carregar condição de pagamento.', AlertType.error);
        this.voltar();
      }
    });
  }

  public get diasArray(): FormArray {
    return this.form.get('dias') as FormArray;
  }

  public get modoAtual(): number {
    return this.form.get('idCondicaoPagamentoModo')?.value;
  }

  public get modoUnico(): boolean {
    return this.modoAtual === CondicaoPagamentoModoEnum.IntervaloUnico;
  }

  public get modoLista(): boolean {
    return this.modoAtual === CondicaoPagamentoModoEnum.DiasPorParcela;
  }

  public selecionarModo(idModo: number): void {
    this.form.get('idCondicaoPagamentoModo')?.setValue(idModo);
  }

  private onModoChange(): void {
    // Story 2.2: ao passar para "Dias por parcela", a lista nasce preenchida com os dias que o
    // intervalo único produzia — não preserva um estado anterior de outro modo.
    if (this.modoLista) {
      this.regenerarDiasDoIntervalo();
    }
  }

  private onNumeroParcelasChange(): void {
    if (this.modoLista) {
      this.sincronizarDiasArrayPreservando();
    }
  }

  private quantidadeParcelas(): number {
    return Math.max(1, Math.round(Number(this.form.get('numeroParcelas')?.value) || 1));
  }

  private intervaloDiasAtual(): number {
    return Math.max(0, Number(this.form.get('intervaloDias')?.value) || 0);
  }

  private regenerarDiasDoIntervalo(): void {
    const qtd = this.quantidadeParcelas();
    const intervalo = this.intervaloDiasAtual();
    this.diasArray.clear();
    for (let i = 0; i < qtd; i++) {
      this.diasArray.push(this.formBuilder.control((i + 1) * intervalo));
    }
  }

  // Acrescenta ou remove posições da lista ao alterar numeroParcelas, preservando as já preenchidas.
  private sincronizarDiasArrayPreservando(): void {
    const qtd = this.quantidadeParcelas();
    const intervalo = this.intervaloDiasAtual();
    const atual = this.diasArray.controls.map(c => c.value);

    const novo: number[] = [];
    for (let i = 0; i < qtd; i++) {
      novo.push(atual[i] !== undefined && atual[i] !== null ? atual[i] : (i + 1) * intervalo);
    }

    this.diasArray.clear();
    novo.forEach(d => this.diasArray.push(this.formBuilder.control(d)));
  }

  /** Dias de vencimento calculados para a prévia — mesma regra do backend (Story, seção 2.2). */
  private diasVencimentoAtual(): number[] {
    if (this.modoLista) {
      return this.diasArray.controls.map(c => Math.round(Number(c.value) || 0));
    }

    const qtd = this.quantidadeParcelas();
    const intervalo = this.intervaloDiasAtual();
    const dias: number[] = [];
    for (let i = 0; i < qtd; i++) {
      dias.push((i + 1) * intervalo);
    }
    return dias;
  }

  private somarDias(dias: number): Date {
    const data = new Date();
    data.setDate(data.getDate() + dias);
    return data;
  }

  public get valorReferenciaPrevia(): number {
    return VALOR_REFERENCIA_PREVIA;
  }

  public get taxaJurosPrevia(): number {
    return Number(this.form.get('taxaJuros')?.value) || 0;
  }

  public get totalComJurosPrevia(): number {
    return UtilService.arredondarAbnt(VALOR_REFERENCIA_PREVIA * (1 + this.taxaJurosPrevia / 100), 2);
  }

  public previaVencimentos(): ParcelaPrevia[] {
    const dias = this.diasVencimentoAtual();
    const qtd = dias.length;
    if (qtd === 0) return [];

    const totalComJuros = this.totalComJurosPrevia;
    const baseParcela = UtilService.arredondarAbnt(totalComJuros / qtd, 2);

    return dias.map((d, i) => ({
      rotulo: `Parcela ${i + 1}/${qtd}`,
      data: this.somarDias(d).toLocaleDateString('pt-BR'),
      // A última parcela absorve a diferença de arredondamento, para a soma fechar exatamente com o total.
      valor: i === qtd - 1 ? UtilService.arredondarAbnt(totalComJuros - baseParcela * (qtd - 1), 2) : baseParcela,
      diasLabel: d === 0 ? 'à vista' : `${d} dias`,
    }));
  }

  public salvar(): void {
    this.form.markAllAsTouched();

    const nomeControl = this.form.get('nome');
    const nome = (nomeControl?.value || '').trim();
    if (nomeControl?.invalid || !nome) {
      this.alertService.show('Revise o formulário', 'Informe o nome da condição para salvar.', AlertType.warning);
      return;
    }

    const numeroParcelasBruto = Number(this.form.get('numeroParcelas')?.value);
    if (!numeroParcelasBruto || numeroParcelasBruto < 1) {
      this.alertService.show('Revise o formulário', 'O número de parcelas deve ser no mínimo 1.', AlertType.warning);
      return;
    }
    const numeroParcelas = this.quantidadeParcelas();

    const intervaloDias = Number(this.form.get('intervaloDias')?.value) || 0;
    if (this.modoUnico && intervaloDias < 0) {
      this.alertService.show('Revise o formulário', 'O intervalo entre parcelas não pode ser negativo.', AlertType.warning);
      return;
    }

    if (this.modoLista && this.diasArray.value.some((d: any) => Number(d) < 0)) {
      this.alertService.show('Revise o formulário', 'Os dias de cada parcela não podem ser negativos.', AlertType.warning);
      return;
    }

    const taxaJuros = Number(this.form.get('taxaJuros')?.value) || 0;
    if (taxaJuros < 0) {
      this.alertService.show('Revise o formulário', 'A taxa de juros não pode ser negativa.', AlertType.warning);
      return;
    }

    const request: CondicaoPagamentoRequest = {
      nome,
      idCondicaoPagamentoModo: this.modoAtual,
      numeroParcelas,
      intervaloDias,
      diasPorParcela: this.modoLista ? this.diasArray.value.map((d: any) => Math.round(Number(d) || 0)) : null,
      taxaJuros,
      isAtivo: this.form.get('isAtivo')?.value,
      idsFormaRecebimento: this.form.get('idsFormaRecebimento')?.value || [],
    };

    this.isSaving = true;
    const salvar$ = this.isEdicao
      ? this.condicaoPagamentoService.put(this.idCondicaoPagamento, request)
      : this.condicaoPagamentoService.post(request);

    salvar$.subscribe({
      next: res => {
        this.isSaving = false;
        if (res.isError) {
          this.alertService.show('Erro', res.errorDescription || 'Erro ao salvar condição de pagamento.', AlertType.error);
          return;
        }

        this.alertService.show(
          this.isEdicao ? 'Condição salva' : 'Condição cadastrada',
          this.isEdicao
            ? `${nome} foi atualizada.`
            : `${nome} está disponível para as formas com parcelamento configurável pela empresa.`,
          AlertType.success
        );
        this.voltar();
      },
      error: () => {
        this.isSaving = false;
        this.alertService.show('Erro', 'Erro ao salvar condição de pagamento.', AlertType.error);
      }
    });
  }

  public voltar(): void {
    this.router.navigate(['/configuracoes/financas/condicoes-pagamento']);
  }
}
